// Helpers for the WLS booking date range that is sent to
// /api/wls/:org/checkout/availability as `booking_range`.
// Framework-free so the module can be required directly from node specs.

const DATE_KEY_FORMAT = 'DD-MM-YYYY';

const DATE_RANGE_ERRORS = {
  missing: 'missing',
  invalid: 'invalid',
  reversed: 'reversed',
  sameDay: 'same_day'
};

const isUsableDate = date =>
  !!date &&
  typeof date.isBefore === 'function' &&
  (typeof date.isValid !== 'function' || date.isValid());

// Returns null for a bookable range, otherwise one of DATE_RANGE_ERRORS.
// `missing` means one or both dates have not been chosen yet (e.g. the
// picker is mid-selection) and is not something to show the guest.
const validateDateRange = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return DATE_RANGE_ERRORS.missing;
  if (!isUsableDate(checkIn) || !isUsableDate(checkOut)) {
    return DATE_RANGE_ERRORS.invalid;
  }
  if (checkOut.isBefore(checkIn, 'day')) return DATE_RANGE_ERRORS.reversed;
  if (checkOut.isSame(checkIn, 'day')) return DATE_RANGE_ERRORS.sameDay;
  return null;
};

const isDisplayableDateRangeError = error =>
  !!error && error !== DATE_RANGE_ERRORS.missing;

// Inclusive list of `{ key, day }` entries from check-in through check-out,
// or null when the range is not valid.
const buildBookingRange = (checkIn, checkOut) => {
  if (validateDateRange(checkIn, checkOut)) return null;

  const bookingRange = [];
  const d = checkIn.clone();
  while (!d.isAfter(checkOut, 'day')) {
    bookingRange.push({ key: d.format(DATE_KEY_FORMAT), day: d.day() });
    d.add(1, 'days');
  }
  return bookingRange;
};

// Availability-shaped object so the existing BookingErrors components can
// render a translated message without a request being made. `client_error`
// marks it as built client-side; `invalid_dates` is null when the error is
// not about the date range.
const invalidDatesAvailability = (reason, errorMessage = null) => ({
  bookable: false,
  client_error: true,
  invalid_dates: reason,
  error_message: errorMessage
});

const isClientAvailabilityError = availability =>
  !!availability && availability.client_error === true;

// Server messages that describe the booking date range rather than some
// other part of the request (e.g. "unit_id is required").
const BOOKING_RANGE_MESSAGE = /booking_range|check_in|check_out|check-in|check-out/i;

const isBookingRangeMessage = message =>
  typeof message === 'string' && BOOKING_RANGE_MESSAGE.test(message);

// Maps an axios rejection from the availability endpoint onto the same
// availability-shaped object. Returns null for anything that is not a 400
// carrying an `error_message`, so callers can keep their existing handling.
const availabilityFromError = (error, { showServerMessage = false } = {}) => {
  const response = error && error.response;
  const errorMessage = response && response.data && response.data.error_message;
  if (!response || response.status !== 400 || !errorMessage) return null;
  const isDateError = isBookingRangeMessage(errorMessage);
  return {
    ...invalidDatesAvailability(
      isDateError ? DATE_RANGE_ERRORS.invalid : null,
      errorMessage
    ),
    ...(showServerMessage && !isDateError && { display_message: errorMessage })
  };
};

const availabilityErrorKey = availability => {
  const reason = availability && availability.invalid_dates;
  if (reason === DATE_RANGE_ERRORS.reversed) {
    return 'cx.errors.listing_dates_reversed';
  }
  if (reason === DATE_RANGE_ERRORS.sameDay) {
    return 'cx.errors.listing_dates_same_day';
  }
  if (reason) return 'cx.errors.listing_dates_invalid';
  return 'cx.errors.listing_generic';
};

// Text shown to the guest: a server validation message that was marked for
// display (e.g. "Number of guests exceeds unit capacity"), otherwise the
// translated date/generic message.
const availabilityErrorText = (availability, translate) =>
  (isClientAvailabilityError(availability) && availability.display_message) ||
  translate(availabilityErrorKey(availability));

// Tracks the newest request of one kind (availability, pricing) so a slower
// reply from an earlier request — e.g. for a previous guest count — is ignored.
const createRequestTracker = () => {
  let latest = 0;
  return {
    start: () => {
      latest += 1;
      return latest;
    },
    isLatest: token => token === latest
  };
};

module.exports = {
  DATE_KEY_FORMAT,
  DATE_RANGE_ERRORS,
  availabilityErrorKey,
  availabilityErrorText,
  availabilityFromError,
  buildBookingRange,
  createRequestTracker,
  invalidDatesAvailability,
  isBookingRangeMessage,
  isClientAvailabilityError,
  isDisplayableDateRangeError,
  validateDateRange
};
